import React, { useState, useEffect, useMemo } from 'react';
import { MasterReportTemplate } from '../../types';
import { LocalAnalyticsContainer } from '../analytics/LocalAnalyticsContainer';

export type { MasterReportTemplate };
export const MASTER_REPORT_TEMPLATES_KEY = 'klydapp_master_report_templates';

// Default corporate standard templates (empty to leave only user-created reports)
export const DEFAULT_MASTER_REPORT_TEMPLATES: MasterReportTemplate[] = [];

export const LEGACY_DEFAULT_REPORT_IDS = [
    'tpl-blank',
    'tpl-executive',
    'tpl-subcontract',
    'tpl-progress',
    'tpl-ssoma-restrictions'
];

// Helper to load templates from localStorage
export const loadMasterReportTemplates = (): MasterReportTemplate[] => {
    try {
        const saved = localStorage.getItem(MASTER_REPORT_TEMPLATES_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed)) {
                const userCreated = parsed.filter(
                    (t: MasterReportTemplate) => !LEGACY_DEFAULT_REPORT_IDS.includes(t.id) && !t.isDefault
                );
                if (userCreated.length !== parsed.length) {
                    try {
                        localStorage.setItem(MASTER_REPORT_TEMPLATES_KEY, JSON.stringify(userCreated));
                    } catch {}
                }
                return userCreated;
            }
        }
    } catch (e) {
        console.error('Error loading master report templates:', e);
    }
    return [];
};

// Helper to save templates to localStorage
export const saveMasterReportTemplates = (templates: MasterReportTemplate[]) => {
    try {
        localStorage.setItem(MASTER_REPORT_TEMPLATES_KEY, JSON.stringify(templates));
    } catch (e) {
        console.error('Error saving master report templates:', e);
    }
};

export const ReportTemplateManager: React.FC = () => {
    const [templates, setTemplates] = useState<MasterReportTemplate[]>(() => loadMasterReportTemplates());
    const [activeView, setActiveView] = useState<'catalog' | 'designer'>('catalog');
    const [editingTemplate, setEditingTemplate] = useState<MasterReportTemplate | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string>('Todas');
    const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
    const [saveNotification, setSaveNotification] = useState<string | null>(null);

    // Save changes to localStorage whenever templates update
    useEffect(() => {
        saveMasterReportTemplates(templates);
    }, [templates]);

    // Filtered templates for catalog
    const filteredTemplates = useMemo(() => {
        return templates.filter(t => {
            const matchSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                (t.description || '').toLowerCase().includes(searchTerm.toLowerCase());
            const matchCat = categoryFilter === 'Todas' || t.category === categoryFilter;
            return matchSearch && matchCat;
        });
    }, [templates, searchTerm, categoryFilter]);

    // Categories list
    const categories = ['Todas', 'Ejecutivo', 'Operativo', 'Producción', 'Calidad', 'General'];

    // Actions
    const handleCreateNewTemplate = () => {
        const newTpl: MasterReportTemplate = {
            id: `tpl-${Date.now()}`,
            name: 'Nueva Plantilla Estándar',
            description: 'Plantilla de reporte estándar configurada por la administración.',
            category: 'General',
            badge: 'Personalizado',
            icon: 'analytics',
            widgets: [
                {
                    id: `w-banner-${Date.now()}`,
                    type: 'header-banner',
                    componentId: 'scurve',
                    colSpan: 12,
                    rowSpan: 1,
                    gridColStart: 1,
                    gridRowStart: 1,
                    style: {
                        bannerTitle: 'TÍTULO DEL REPORTE',
                        bannerSubtitle: 'Subtítulo o descripción de indicadores de obra'
                    }
                }
            ],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };
        setEditingTemplate(newTpl);
        setActiveView('designer');
    };

    const handleEditTemplate = (tpl: MasterReportTemplate) => {
        setEditingTemplate(JSON.parse(JSON.stringify(tpl)));
        setSelectedWidgetId(tpl.widgets[0]?.id || null);
        setActiveView('designer');
    };

    const handleDuplicateTemplate = (tpl: MasterReportTemplate) => {
        const copy: MasterReportTemplate = {
            ...JSON.parse(JSON.stringify(tpl)),
            id: `tpl-${Date.now()}`,
            name: `${tpl.name} (Copia)`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };
        setTemplates(prev => [copy, ...prev]);
        setSaveNotification(`Plantilla "${tpl.name}" duplicada correctamente.`);
        setTimeout(() => setSaveNotification(null), 3000);
    };

    const handleDeleteTemplate = (id: string, name: string) => {
        if (confirm(`¿Estás seguro de que deseas eliminar la plantilla estándar "${name}"?`)) {
            setTemplates(prev => prev.filter(t => t.id !== id));
            setSaveNotification(`Plantilla "${name}" eliminada.`);
            setTimeout(() => setSaveNotification(null), 3000);
        }
    };

    const handleRestoreDefaults = () => {
        if (confirm('¿Deseas restaurar el catálogo maestro a las plantillas estándar corporativas predeterminadas? Se conservarán las plantillas nuevas creadas.')) {
            setTemplates(prev => {
                const existingCustom = prev.filter(t => !DEFAULT_MASTER_REPORT_TEMPLATES.some(d => d.id === t.id));
                return [...DEFAULT_MASTER_REPORT_TEMPLATES, ...existingCustom];
            });
            setSaveNotification('Catálogo de plantillas predeterminadas restaurado.');
            setTimeout(() => setSaveNotification(null), 3000);
        }
    };

    // Designer Save
    const handleSaveFromDesigner = () => {
        if (!editingTemplate) return;
        const updated = {
            ...editingTemplate,
            updatedAt: new Date().toISOString()
        };
        setTemplates(prev => {
            const exists = prev.some(t => t.id === updated.id);
            if (exists) {
                return prev.map(t => t.id === updated.id ? updated : t);
            }
            return [updated, ...prev];
        });
        setSaveNotification(`Plantilla "${updated.name}" guardada en el catálogo.`);
        setTimeout(() => setSaveNotification(null), 3000);
        setActiveView('catalog');
        setEditingTemplate(null);
    };

    // Designer Widget Management
    const handleAddWidgetToDesigner = (type: string, title: string) => {
        if (!editingTemplate) return;
        const newWidgetId = `w-${Date.now()}`;
        
        let colSpan = 6;
        let rowSpan = 3;
        let gridColStart = 1;
        let gridRowStart = 2;

        if (type === 'header-banner') {
            colSpan = 12;
            rowSpan = 1;
            gridColStart = 1;
            gridRowStart = 1;
        } else if (type === 'kpi-card') {
            colSpan = 3;
            rowSpan = 2;
        } else if (type === 'slicer') {
            colSpan = 4;
            rowSpan = 5;
        }

        const newWidget = {
            id: newWidgetId,
            type,
            componentId: 'scurve',
            colSpan,
            rowSpan,
            gridColStart,
            gridRowStart,
            style: {
                showTitle: true,
                customTitle: title,
                bannerTitle: type === 'header-banner' ? title : undefined,
                bannerSubtitle: type === 'header-banner' ? 'Indicadores y métricas de gestión' : undefined
            }
        };

        setEditingTemplate({
            ...editingTemplate,
            widgets: [...editingTemplate.widgets, newWidget]
        });
        setSelectedWidgetId(newWidgetId);
    };

    const handleUpdateWidgetInDesigner = (widgetId: string, patch: any) => {
        if (!editingTemplate) return;
        setEditingTemplate({
            ...editingTemplate,
            widgets: editingTemplate.widgets.map(w => w.id === widgetId ? { ...w, ...patch } : w)
        });
    };

    const handleDeleteWidgetInDesigner = (widgetId: string) => {
        if (!editingTemplate) return;
        setEditingTemplate({
            ...editingTemplate,
            widgets: editingTemplate.widgets.filter(w => w.id !== widgetId)
        });
        if (selectedWidgetId === widgetId) {
            setSelectedWidgetId(null);
        }
    };

    // -------------------------------------------------------------
    // RENDER VISTA 1: CATÁLOGO DE REPORTES ESTÁNDAR
    // -------------------------------------------------------------
    if (activeView === 'catalog') {
        return (
            <div className="space-y-6">
                {/* Header Superior del Maestro */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-5">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-primary text-[28px]">auto_stories</span>
                            <h2 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">
                                Maestro de Reportes y Plantillas Estándar
                            </h2>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            Configura las plantillas corporativas estándar que estarán disponibles en el módulo de diapositivas y analítica de todas las obras.
                        </p>
                    </div>

                    <div className="flex items-center gap-2.5">
                        {saveNotification && (
                            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 rounded-xl border border-emerald-200 dark:border-emerald-800 animate-in fade-in">
                                <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                {saveNotification}
                            </span>
                        )}

                        <button
                            onClick={handleRestoreDefaults}
                            className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition-all cursor-pointer"
                            title="Restablecer catálogo predeterminado"
                        >
                            <span className="material-symbols-outlined text-[18px]">restart_alt</span>
                            Restaurar Estándares
                        </button>

                        <button
                            onClick={handleCreateNewTemplate}
                            className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/95 text-white rounded-xl text-xs font-bold shadow-md shadow-primary/20 transition-all active:scale-95 cursor-pointer"
                        >
                            <span className="material-symbols-outlined text-[18px]">add_circle</span>
                            + Nueva Plantilla
                        </button>
                    </div>
                </div>

                {/* Filtros y Buscador */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-50 dark:bg-slate-850 p-3 rounded-2xl border border-slate-200 dark:border-slate-800">
                    <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
                        {categories.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setCategoryFilter(cat)}
                                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                                    categoryFilter === cat
                                        ? 'bg-primary text-white shadow-sm'
                                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                                }`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>

                    <div className="relative w-full sm:w-64">
                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
                        <input
                            type="text"
                            placeholder="Buscar plantilla estándar..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-primary"
                        />
                    </div>
                </div>

                {filteredTemplates.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-12 text-center bg-slate-50 dark:bg-slate-850/50 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700">
                        <div className="size-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-3">
                            <span className="material-symbols-outlined text-[32px]">auto_stories</span>
                        </div>
                        <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">
                            No hay plantillas de reportes disponibles
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mt-1 mb-5">
                            Crea una nueva plantilla estándar personalizada para comenzar a generar reportes en las obras.
                        </p>
                        <button
                            onClick={handleCreateNewTemplate}
                            className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/95 text-white rounded-xl text-xs font-bold shadow-md shadow-primary/20 transition-all active:scale-95 cursor-pointer"
                        >
                            <span className="material-symbols-outlined text-[18px]">add_circle</span>
                            + Nueva Plantilla
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {filteredTemplates.map(tpl => (
                            <div
                                key={tpl.id}
                                className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 flex flex-col justify-between gap-4 shadow-sm hover:shadow-lg hover:border-primary/50 transition-all group"
                            >
                                <div className="space-y-3">
                                    {/* Header Card */}
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-center gap-2.5">
                                            <div className="size-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                                                <span className="material-symbols-outlined text-[22px]">{tpl.icon || 'analytics'}</span>
                                            </div>
                                            <div>
                                                <h3 className="font-extrabold text-sm text-slate-900 dark:text-white group-hover:text-primary transition-colors">
                                                    {tpl.name}
                                                </h3>
                                                <span className="text-[10px] font-semibold text-slate-400">
                                                    {tpl.category} • {tpl.widgets.length} componente{tpl.widgets.length !== 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        </div>

                                        {tpl.badge && (
                                            <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                                                {tpl.badge}
                                            </span>
                                        )}
                                    </div>

                                    {/* Descripción */}
                                    <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
                                        {tpl.description}
                                    </p>

                                    {/* Mini Previsualización de la Grilla 16:9 */}
                                    <div className="aspect-video w-full bg-slate-100 dark:bg-slate-950/70 rounded-xl border border-slate-200 dark:border-slate-800 p-2 relative overflow-hidden">
                                        <div className="grid grid-cols-12 grid-rows-6 gap-1 w-full h-full">
                                            {tpl.widgets.map((w, idx) => (
                                                <div
                                                    key={w.id || idx}
                                                    style={{
                                                        gridColumnStart: w.gridColStart || 1,
                                                        gridColumnEnd: `span ${w.colSpan || 4}`,
                                                        gridRowStart: w.gridRowStart || 1,
                                                        gridRowEnd: `span ${w.rowSpan || 2}`
                                                    }}
                                                    className="bg-primary/20 dark:bg-primary/30 border border-primary/40 rounded flex items-center justify-center text-[8px] font-bold text-primary truncate px-1"
                                                >
                                                    {w.style?.customTitle || w.style?.bannerTitle || w.type}
                                                </div>
                                            ))}
                                        </div>
                                        {tpl.widgets.length === 0 && (
                                            <div className="absolute inset-0 flex items-center justify-center text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                                                Lienzo en Blanco
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Acciones de la Tarjeta */}
                                <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800 text-xs">
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => handleDuplicateTemplate(tpl)}
                                            className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
                                            title="Duplicar plantilla"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">content_copy</span>
                                        </button>
                                        <button
                                            onClick={() => handleDeleteTemplate(tpl.id, tpl.name)}
                                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors cursor-pointer"
                                            title="Eliminar plantilla"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                        </button>
                                    </div>

                                    <button
                                        onClick={() => handleEditTemplate(tpl)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 hover:bg-primary text-primary hover:text-white rounded-xl font-bold text-xs transition-all active:scale-95 cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">edit</span>
                                        Diseñar Plantilla
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // -------------------------------------------------------------
    // RENDER VISTA 2: DISEÑADOR COMPLETO EN PANTALLA COMPLETA
    // -------------------------------------------------------------
    return (
        <div className="fixed inset-0 z-[150] bg-slate-100 dark:bg-slate-900 overflow-hidden flex flex-col animate-in fade-in duration-200">
            <LocalAnalyticsContainer
                initialMode="edit"
                context="designer"
                action={editingTemplate ? "edit" : "create"}
                canDesign={true}
                canModifyDesign={true}
                initialName={editingTemplate?.name || 'Nueva Plantilla Estándar'}
                initialDescription={editingTemplate?.description || ''}
                initialCategory={editingTemplate?.category || 'General'}
                initialBadge={editingTemplate?.badge || 'Personalizado'}
                initialWidgets={editingTemplate?.widgets || []}
                templateId={editingTemplate?.id}
                onSaveTemplate={(savedTpl) => {
                    setTemplates(prev => {
                        const exists = prev.some(t => t.id === savedTpl.id);
                        if (exists) {
                            return prev.map(t => t.id === savedTpl.id ? savedTpl : t);
                        }
                        return [savedTpl, ...prev];
                    });
                    setSaveNotification(`Plantilla "${savedTpl.name}" guardada en el catálogo.`);
                    setTimeout(() => setSaveNotification(null), 3000);
                    setActiveView('catalog');
                    setEditingTemplate(null);
                }}
                onCancel={() => {
                    setActiveView('catalog');
                    setEditingTemplate(null);
                }}
            />
        </div>
    );
};

export default ReportTemplateManager;
